/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import { v2 } from "@prftesp/cle-logger";
import { useEffect, useRef, useState } from "react";
import { getConfigValue } from "../sharedServices/configService";
import { getLoggedInUserEmail, getUserJwt } from "../utility/auth";
import { URL_CONFIG_KEYS } from "../utility/constants";
import errorCodes from "../utility/errorCodes";

const COMPONENT_NAME = "hooks/useWebsocket";

/**
 * @typedef WebsocketClient
 * @type {Object}
 * @property {(cb:Function) => () => void} subscribe Register message event callback
 */

/**
 * Starts and maintains the websocket connection, handles event subscriptions,
 * and logic to reconnect if the websocket closes.
 * @param {string} url ws url
 * @returns {WebsocketClient}
 */
const connectWebsocket = (url) => {
	let ws = null;
	let messageListeners = [];

	/**
	 * Register message event callback
	 * @param {Function} cb callback function that will receive message data
	 * @returns {() => void} unsubscribe function
	 */
	const subscribe = (cb) => {
		messageListeners.push(cb);

		return () => {
			messageListeners = messageListeners.filter((x) => x !== cb);
		};
	};

	/**
	 * Connect to websocket and setup event listeners
	 */
	const connect = async () => {
		// important to get a new auth token before reconnecting, old token might
		// have expired and Amplify Auth library will internally handle token refresh
		const authToken = await getUserJwt();
		ws = new WebSocket(`${url}&token=${authToken}`);

		v2.verbose("Websocket connecting.", {
			componentName: COMPONENT_NAME,
			additionalData: {},
		});

		ws.onopen = (event) => {
			v2.verbose("Websocket open.", {
				componentName: COMPONENT_NAME,
				additionalData: { event },
			});
		};

		ws.onmessage = (event) => {
			messageListeners.forEach((cb) => cb(JSON.parse(event.data)));
		};

		ws.onerror = (event) => {
			v2.error("Error in websocket.", {
				componentName: COMPONENT_NAME,
				logCode: errorCodes.SET_AGENT_OFFLINE_ERROR,
				additionalData: { event },
			});
		};

		ws.onclose = (event) => {
			v2.verbose("Websocket closed.", {
				componentName: COMPONENT_NAME,
				additionalData: { event },
			});

			// attempt reconnect after 5 seconds
			setTimeout(connect, 5000);
		};
	};

	// initialize connection
	connect();

	return {
		subscribe,
	};
};

/**
 * global connected websocket instance
 * @type {WebsocketClient}
 */
let wsClient = null;

/**
 * Initializes websocket client from config
 */
const initWs = async () => {
	const [wsUrlConfig, userId] = await Promise.all([
		getConfigValue(URL_CONFIG_KEYS.SHARED_WEBSOCKET_API_URL),
		getLoggedInUserEmail(),
	]);

	wsClient = connectWebsocket(`${wsUrlConfig[URL_CONFIG_KEYS.SHARED_WEBSOCKET_API_URL]}?userId=${userId}`);
};

/**
 * Initializes websocket if not already created and provides
 * a way to subscribe callbacks to messages of a specific event type
 * @param {string} type websocket event type
 * @param {Function} cb callback that will receive message data
 */
export const useWebsocketEvent = (type, cb) => {
	const stableCallback = useRef(cb);
	const [isInitialized, setIsInitialized] = useState(false);

	// update callback ref as the function changes to
	// always have the latest instance and avoid stale
	// state access in the calling component
	useEffect(() => {
		stableCallback.current = cb;
	}, [cb]);

	// initialize the global websocket if not already created
	useEffect(() => {
		if (wsClient) {
			setIsInitialized(true);
			return;
		}
		initWs().then(() => setIsInitialized(true));
	}, []);

	// register event listener for the specified message type
	useEffect(() => {
		if (!isInitialized) {
			return;
		}

		const handleMessage = (data) => {
			if (data.type === type) {
				stableCallback.current(data);
			}
		};

		return wsClient.subscribe(handleMessage);
	}, [type, isInitialized]);
};
